import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
} from '@mui/material';
import { TrendingUp, ShoppingCart, People, AttachMoney } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { SalesService } from '../services/salesService';
import { InventoryService } from '../services/inventoryService';
import { CustomerService } from '../services/customerService';
import { Sale, Product, Customer } from '../types';

export default function Reports() {
  const { appUser } = useAuth();
  const [sales, setSales] = useState<Sale[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadReportData = async () => {
    if (!appUser?.salonId) {
      setError('User not properly authenticated');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [salesData, productsData, customersData] = await Promise.all([
        SalesService.getSales(appUser.salonId),
        InventoryService.getProducts(appUser.salonId),
        CustomerService.getCustomers(appUser.salonId),
      ]);

      setSales(salesData);
      setProducts(productsData);
      setCustomers(customersData);
      setError(null);
    } catch (err) {
      console.error('Error loading report data:', err);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReportData();
  }, [appUser]);

  // Calculate metrics
  const totalRevenue = sales.reduce((sum, sale) => sum + sale.totalAmount, 0);
  const totalTransactions = sales.length;
  const averageTransaction = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;
  const totalInventoryValue = products.reduce((sum, product) =>
    sum + (product.cost * product.currentStock), 0
  );
  const lowStockProducts = products.filter(product => product.currentStock <= product.minStockLevel);
  const topCustomers = customers
    .sort((a, b) => b.totalSpent - a.totalSpent)
    .slice(0, 5);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Reports & Analytics
        </Typography>
        <Typography>Loading reports...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Reports & Analytics
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <AttachMoney color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Revenue</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {formatCurrency(totalRevenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <ShoppingCart color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Transactions</Typography>
              </Box>
              <Typography variant="h4" color="secondary">
                {totalTransactions}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <TrendingUp color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Avg Transaction</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {formatCurrency(averageTransaction)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <People color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Customers</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {customers.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Reports Grid */}
      <Grid container spacing={3}>
        {/* Top Customers */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Customers
              </Typography>
              {topCustomers.length === 0 ? (
                <Typography color="text.secondary">No customer data available</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell align="right">Total Spent</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topCustomers.map((customer) => (
                        <TableRow key={customer.id}>
                          <TableCell>{customer.name}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(customer.totalSpent)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Low Stock Alert */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Low Stock Alert
              </Typography>
              {lowStockProducts.length === 0 ? (
                <Typography color="text.secondary">All products are well stocked</Typography>
              ) : (
                <Box>
                  {lowStockProducts.map((product) => (
                    <Box key={product.id} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2">{product.name}</Typography>
                      <Chip
                        label={`${product.currentStock} left`}
                        size="small"
                        color="error"
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Sales */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Sales Transactions
              </Typography>
              {sales.length === 0 ? (
                <Typography color="text.secondary">No sales data available</Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Items</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell>Payment</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sales.slice(0, 10).map((sale) => (
                        <TableRow key={sale.id}>
                          <TableCell>
                            {sale.createdAt.toLocaleDateString('en-NG')}
                          </TableCell>
                          <TableCell>
                            {sale.items.length} item{sale.items.length !== 1 ? 's' : ''}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(sale.totalAmount)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={sale.paymentMethod}
                              size="small"
                              color={sale.paymentMethod === 'cash' ? 'success' : 'primary'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
